(() => {
  const root = (window.KOMModules = window.KOMModules || {});

  root.setupAdminAuthSwitcher = () => {
    const rootNode = document.querySelector('[data-auth-switcher]');
    if (!rootNode) {
      return;
    }

    const panels = Array.from(rootNode.querySelectorAll('[data-auth-panel]'));
    const modeButtons = Array.from(rootNode.querySelectorAll('[data-auth-mode]'));
    const defaultMode = rootNode.getAttribute('data-default-mode') || 'password';

    const setMode = (mode) => {
      panels.forEach((panel) => {
        const panelMode = panel.getAttribute('data-auth-panel');
        const isActive = panelMode === mode;

        panel.classList.toggle('is-hidden', !isActive);
        panel.hidden = !isActive;
      });

      modeButtons.forEach((button) => {
        const buttonMode = button.getAttribute('data-auth-mode');
        const isActive = buttonMode === mode;

        button.classList.toggle('is-active', isActive);
        button.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });
    };

    modeButtons.forEach((button) => {
      button.addEventListener('click', () => {
        const mode = button.getAttribute('data-auth-mode') || 'password';
        setMode(mode);
      });
    });

    setMode(defaultMode);
  };

  root.setupAdminSeoCrud = () => {
    const rootNode = document.querySelector('[data-seo-crud]');
    if (!rootNode) {
      return;
    }

    const drawer = rootNode.querySelector('[data-seo-drawer]');
    const quickForm = rootNode.querySelector('[data-seo-quick-form]');
    const openButtons = Array.from(rootNode.querySelectorAll('[data-seo-open]'));
    const closeButtons = Array.from(rootNode.querySelectorAll('[data-seo-close]'));
    if (!drawer || !quickForm || openButtons.length === 0) {
      return;
    }

    const byName = (name) => quickForm.querySelector(`[name="${name}"]`);
    const slugInput = byName('slug');
    const localeInput = byName('locale');
    const canonicalPreview = quickForm.querySelector('[data-seo-canonical-display]');
    const sectionDisplay = quickForm.querySelector('[data-seo-section-display]');
    const ogModeInputs = Array.from(quickForm.querySelectorAll('input[name="og_image_mode"]'));
    const ogImageInput = byName('og_image_input');
    const ogDefaultPath = quickForm.querySelector('[data-og-default-path]');
    const ogCustomUpload = quickForm.querySelector('[data-og-custom-upload]');
    const robotsInputs = Array.from(quickForm.querySelectorAll('input[name="robots_flags[]"]'));

    const canonicalFrom = () => {
      if (!canonicalPreview || !localeInput) {
        return;
      }

      const locale = (localeInput.value || 'es').trim().toLowerCase();
      const slug = (slugInput?.value || '').trim().replace(/^\/+|\/+$/g, '');
      canonicalPreview.textContent = slug === '' ? `/${locale}` : `/${locale}/${slug}`;
    };

    const syncRobots = (robots) => {
      const raw = (robots || '').toLowerCase();
      const hasNoIndex = raw.includes('noindex');
      const hasNoFollow = raw.includes('nofollow');

      robotsInputs.forEach((input) => {
        if (input.value === 'index') {
          input.checked = !hasNoIndex;
        }

        if (input.value === 'follow') {
          input.checked = !hasNoFollow;
        }
      });
    };

    const syncOgPlaceholder = () => {
      if (!ogImageInput || !ogCustomUpload) {
        return;
      }

      const selected = ogModeInputs.find((item) => item.checked)?.value || 'default';
      if (selected === 'default') {
        ogCustomUpload.hidden = true;
        ogImageInput.value = '';
      } else {
        ogCustomUpload.hidden = false;
      }
    };

    const openDrawer = () => {
      drawer.hidden = false;
      document.body.classList.add('admin-drawer-open');
    };

    const closeDrawer = () => {
      drawer.hidden = true;
      document.body.classList.remove('admin-drawer-open');
    };

    openButtons.forEach((button) => {
      button.addEventListener('click', () => {
        byName('id').value = button.getAttribute('data-id') || '';
        byName('locale').value = button.getAttribute('data-locale') || 'es';
        if (sectionDisplay) {
          sectionDisplay.textContent = button.getAttribute('data-section') || '-';
        }
        byName('slug').value = button.getAttribute('data-slug') || '';
        byName('meta_title').value = button.getAttribute('data-meta-title') || '';
        byName('meta_description').value = button.getAttribute('data-meta-description') || '';
        syncRobots(button.getAttribute('data-robots') || 'index,follow');

        const ogImage = button.getAttribute('data-og-image') || '';
        const defaultOg = button.getAttribute('data-og-default') || '';
        if (ogDefaultPath) {
          ogDefaultPath.textContent = defaultOg || '/dist/img/og-default.jpg';
        }
        byName('og_image_input').value = ogImage;
        const usesDefault = ogImage === '' || (defaultOg !== '' && ogImage === defaultOg);
        ogModeInputs.forEach((input) => {
          input.checked = input.value === (usesDefault ? 'default' : 'custom');
        });

        syncOgPlaceholder();
        canonicalFrom();
        quickForm.dispatchEvent(new CustomEvent('seo:refresh-scores'));
        openDrawer();
      });
    });

    closeButtons.forEach((button) => {
      button.addEventListener('click', closeDrawer);
    });

    slugInput?.addEventListener('input', canonicalFrom);
    ogModeInputs.forEach((input) => input.addEventListener('change', syncOgPlaceholder));

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !drawer.hidden) {
        closeDrawer();
      }
    });
  };

  root.setupSeoScoring = () => {
    const scoreBlocks = Array.from(document.querySelectorAll('[data-seo-score]'));
    if (scoreBlocks.length === 0) {
      return;
    }

    const classify = (len, goodMin, goodMax, warnMin, warnMax) => {
      if (len >= goodMin && len <= goodMax) {
        return { status: 'ok', label: 'Bien' };
      }

      if (len >= warnMin && len <= warnMax) {
        if (len < goodMin) {
          return { status: 'warn', label: 'Algo corto' };
        }

        return { status: 'warn', label: 'Algo largo' };
      }

      if (len === 0) {
        return { status: 'danger', label: 'Vacio' };
      }

      if (len < warnMin) {
        return { status: 'danger', label: 'Muy corto' };
      }

      return { status: 'danger', label: 'Muy largo' };
    };

    scoreBlocks.forEach((block) => {
      const field = block.getAttribute('data-field');
      if (!field) {
        return;
      }

      const parentForm = block.closest('form');
      const input = parentForm ? parentForm.querySelector(`[name="${field}"]`) : document.querySelector(`[name="${field}"]`);
      if (!input) {
        return;
      }

      const fill = block.querySelector('[data-seo-score-fill]');
      const text = block.querySelector('[data-seo-score-text]');
      if (!fill || !text) {
        return;
      }

      const goodMin = Number(block.getAttribute('data-good-min') || 1);
      const goodMax = Number(block.getAttribute('data-good-max') || 1);
      const warnMin = Number(block.getAttribute('data-warn-min') || 1);
      const warnMax = Number(block.getAttribute('data-warn-max') || goodMax + 20);
      const trackMax = Math.max(warnMax + 30, goodMax + 20);

      const refresh = () => {
        const value = (input.value || '').trim();
        const len = value.length;
        const ratio = Math.min((len / trackMax) * 100, 100);
        const verdict = classify(len, goodMin, goodMax, warnMin, warnMax);

        block.setAttribute('data-status', verdict.status);
        fill.style.width = `${ratio}%`;
        text.textContent = `${field.replace('_', ' ')}: ${len} caracteres (${verdict.label})`;
      };

      input.addEventListener('input', refresh);
      parentForm?.addEventListener('seo:refresh-scores', refresh);
      refresh();
    });
  };

  root.setupOgModeUi = () => {
    const forms = Array.from(document.querySelectorAll('form'));
    forms.forEach((form) => {
      const modeInputs = Array.from(form.querySelectorAll('input[name="og_image_mode"]'));
      const uploadBox = form.querySelector('[data-og-custom-upload]');
      if (modeInputs.length === 0 || !uploadBox) {
        return;
      }

      const refresh = () => {
        const selected = modeInputs.find((item) => item.checked)?.value || 'default';
        uploadBox.hidden = selected !== 'custom';
      };

      modeInputs.forEach((input) => input.addEventListener('change', refresh));
      refresh();
    });
  };

  root.setupFileDropUploads = () => {
    const boxes = Array.from(document.querySelectorAll('[data-drop-upload]'));
    if (boxes.length === 0) {
      return;
    }

    boxes.forEach((box) => {
      const input = box.querySelector('[data-drop-input]');
      if (!input) {
        return;
      }

      box.addEventListener('dragover', (event) => {
        event.preventDefault();
        box.classList.add('is-dragover');
      });

      box.addEventListener('dragleave', () => {
        box.classList.remove('is-dragover');
      });

      box.addEventListener('drop', (event) => {
        event.preventDefault();
        box.classList.remove('is-dragover');
        if (!event.dataTransfer || !event.dataTransfer.files || event.dataTransfer.files.length === 0) {
          return;
        }

        input.files = event.dataTransfer.files;
      });
    });
  };

  root.setupAdminStatsCharts = () => {
    const items = Array.from(document.querySelectorAll('[data-chart-item]'));
    if (items.length === 0) {
      return;
    }

    items.forEach((item) => {
      const value = Number(item.getAttribute('data-value') || 0);
      const max = Number(item.getAttribute('data-max') || 1);
      const ratio = Math.min((value / Math.max(max, 1)) * 100, 100);
      const fill = item.querySelector('[data-chart-fill]');
      if (!fill) {
        return;
      }

      fill.style.width = `${ratio}%`;
    });
  };

  root.setupReactiveSubmits = () => {
    const forms = Array.from(document.querySelectorAll('form[data-reactive-submit]'));
    if (forms.length === 0) {
      return;
    }

    forms.forEach((form) => {
      form.addEventListener('submit', () => {
        const submitButton = form.querySelector('button[type="submit"]');
        if (!submitButton) {
          return;
        }

        const idleText = submitButton.getAttribute('data-submit-label') || submitButton.textContent || 'Guardar';
        const loadingText = submitButton.getAttribute('data-submitting-label') || 'Guardando...';
        submitButton.setAttribute('data-submit-label', idleText);
        submitButton.textContent = loadingText;
        submitButton.disabled = true;
        form.classList.add('is-submitting');
      });
    });
  };
})();
